import { CardFlavours } from '@components/Card2/types';
import { cardEditContext } from '@context/card-edit-context';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { useCardAttributeLock } from './card/use-card-attribute-lock';
import type { CardListProperty, CardProperty, Stage, ValueProvider } from '.cache/__types__';

/** Helpful little fella that gets either the property `value` or `values`
 * depending on if the property is a CardProperty or CardListProperty.
 */
function valueOrValues<T extends string | string[]>(
  property: CardProperty | CardListProperty | undefined,
): T | null {
  if (!property) {
    return null;
  }

  if ('value' in property && typeof property.value === 'string') {
    return property.value as T;
  }

  if ('values' in property && Array.isArray(property.values)) {
    return property.values as T;
  }

  return null;
}

type CardPropertyState<T extends string | string[]> = {
  value: T | null;
  setValue: (value: T | null) => void;
  valueLocked?: boolean;
  setValueLocked?: (isLocked: boolean) => void;
  isOptional?: boolean;
  setIsOptional?: (isOptional: boolean) => void;
  valueProvider: ValueProvider | undefined;
  setValueProvider: (value: ValueProvider) => void;
  instruction?: string;
  setInstruction?: (instruction: string) => void;
  /** Stage shouldn't be changed by the client, we set it on the server after each change to a property */
  stage: Stage | undefined;

  // Just a line break to split up all these lines
  isModified: boolean;
  onCancel: () => void;
  currentlyBeingEdited: boolean;
  isTemplated: boolean;
  /** Boolean for if the property has a value from the property, or it's currently being edited */
  showProperty: boolean;
  showPropertyPlaceholder: boolean;
  valueLockEnabled: boolean;
  /** Use this to decide if you should show an edit popover for a card property at all
   * If the property is fully locked, we shouldn't show it at all.
   */
  fullyLocked?: boolean;
  lockedTooltipMessage?: string;
};

/** Stores state for a CardProperty or CardListProperty
 * By providing the type T you can ensure that the value is either a string or a string[]
 */
export function useCardPropertyState<T extends string | string[]>(
  property: CardProperty | CardListProperty | undefined,
  attributeId: string | undefined,
  currentlyActiveAttributeId: string | undefined,
  defaultTemplateValueProvider?: ValueProvider | undefined,
): CardPropertyState<T> {
  const [value, setValue] = useState<T | null>(null);
  const [valueLocked, setValueLocked] = useState<boolean>(false);
  const [valueProvider, setValueProvider] = useState<ValueProvider | undefined>(undefined);
  const [isOptional, setIsOptional] = useState<boolean>(false);
  const [instruction, setInstruction] = useState<string | undefined>(undefined);
  const { flavour, isCardOwner } = useContext(cardEditContext);

  const isModified = value !== valueOrValues(property);

  const currentlyBeingEdited = Boolean(
    currentlyActiveAttributeId && currentlyActiveAttributeId === attributeId,
  );

  const defaultValueProvider = flavour === 'template' ? defaultTemplateValueProvider : 'admin';

  const [tempValue, setTempValue] = useState<string | string[] | undefined>();

  useEffect(() => {
    setValue(valueOrValues<T>(property));
  }, [property]);

  useEffect(() => {
    setValueLocked(property?.value_locked ?? false);
  }, [property?.value_locked]);

  useEffect(() => {
    setValueProvider(property?.value_provider ?? defaultValueProvider);
  }, [defaultValueProvider, property?.value_provider]);

  useEffect(() => {
    setIsOptional(property?.is_optional ?? false);
  }, [property?.is_optional]);

  useEffect(() => {
    setInstruction(property?.instruction_text ?? undefined);
  }, [property?.instruction_text]);

  const onCancel = () => {
    setValue(valueOrValues<T>(property));
    setValueProvider(property?.value_provider ?? defaultValueProvider);
    setValueLocked(property?.value_locked ?? false);
    setIsOptional(property?.is_optional ?? false);
    setInstruction(property?.instruction_text ?? undefined);
  };

  // When switching between Me and Felicity, or Shard and Individual we need to store the value in a temp state
  // so when you go back to Me or Shared the value is re-instated
  const handleSetValueProvider = useCallback(
    (provider: ValueProvider) => {
      setValueProvider(provider);

      if (provider === 'admin' || provider === 'template') {
        if (tempValue !== undefined) {
          setValue(tempValue as T);
        }
      }

      if (provider === 'member') {
        // Unlock the value when switching to member
        setValueLocked(false);
        if (value) {
          setTempValue(value as T);
          setValue(null);
        }
      }
    },
    [tempValue, value],
  );

  /** Used to decide if the property should show on the card at all */
  const showProperty = useMemo(() => {
    // An existing property must always render, even when a template-provided value is an empty
    // string: hiding it while Add Fields greys out its attribute (because the row exists) leaves
    // admins with a field they can neither see nor re-add (BIZ-2903). The UP identity editor
    // renders empty properties too, so this keeps the two editors consistent.
    return currentlyBeingEdited || Boolean(property);
  }, [currentlyBeingEdited, property]);

  const showPropertyPlaceholder = useMemo(() => {
    if (flavour === CardFlavours.TEMPLATE && valueProvider !== 'template') {
      return true;
    }

    const hasNoValue = !value || value === '' || value.length === 0;

    if (currentlyBeingEdited && hasNoValue) {
      return true;
    }

    if (property?.id && hasNoValue) {
      return true;
    }

    return false;
  }, [flavour, valueProvider, currentlyBeingEdited, value, property?.id]);

  const valueLockEnabled = () => {
    if (flavour === CardFlavours.TEAM && !isCardOwner && (value?.length ?? 0 > 0)) {
      return true;
    }

    return false;
  };

  const { canUnlock, tooltipMessage: lockedTooltipMessage } = useCardAttributeLock({
    flavour,
    valueLocked,
    valueProvider,
    labelLocked: undefined,
    labelLockedByTemplate: undefined,
  });

  return {
    value,
    setValue,
    isModified,
    onCancel,
    currentlyBeingEdited,
    showProperty,
    valueProvider,
    setValueProvider: handleSetValueProvider,
    valueLocked,
    setValueLocked,
    isOptional,
    setIsOptional,
    instruction,
    setInstruction,
    showPropertyPlaceholder,
    valueLockEnabled: valueLockEnabled(),
    isTemplated: Boolean(property?.template_id),
    fullyLocked: !canUnlock,
    lockedTooltipMessage,
    stage: property?.stage ?? undefined,
  };
}
